import isEmpty from 'lodash/isEmpty'
import moment from 'moment'
import { firebaseService } from 'services'

const { getDb, getServerTimestamp } = firebaseService

export function createTestRoomSubscriber({ getAccessLinkApi }) {
  let testRoomListener = null
  let retryCount = 0
  let isCancelled = false
  let timeoutId = null
  let expireTimeoutId = null
  let globalVersion = 0

  const TIMEZONE = 'Asia/Ho_Chi_Minh'
  const getRef = (url) => `/teacher-testers/${url}`

  const delay = (ms) =>
    new Promise((resolve) => {
      timeoutId = setTimeout(resolve, ms)
    })

  const myVersion = ++globalVersion
  const isAlive = () => myVersion === globalVersion

  const cleanup = () => {
    isCancelled = true
    globalVersion++

    if (testRoomListener) {
      testRoomListener.off('value')
      testRoomListener = null
    }

    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }

    if (expireTimeoutId) {
      clearTimeout(expireTimeoutId)
      expireTimeoutId = null
    }
  }

  const tryGetAccessLink = ({ class_id, user_id }) => {
    if (!isAlive()) return

    getAccessLinkApi({
      class_id,
      user_id,
      type: 'moderator',
      callbackSuccess: (link) => {
        window.location.replace(link)
      },
      callBackError: async () => {
        retryCount += 1
        if (retryCount < 3 && !isCancelled) {
          await delay(5000)
          if (isCancelled) return
          return tryGetAccessLink({ class_id, user_id })
        }
        return null
      },
    })
  }

  const subscribe = async ({ currentUserId, bookingInfo, serverOffset = 0, onExpired }) => {
    const {
      begin_at,
      teacher_name,
      schedule_id: scheduleId,
      tester_waiting_room_info,
      current_time,
    } = bookingInfo || {}

    if (testRoomListener || !scheduleId) return

    const fbDatabase = await getDb()

    const datePath = moment(begin_at * 1000)
      .tz(TIMEZONE)
      .format('YYYY-MM-DD')
    const roomListRef = fbDatabase.ref(getRef(`${datePath}/${scheduleId}/room_list`))
    const waitingRoomRef = fbDatabase.ref(
      getRef(`${datePath}/${scheduleId}/teacher_room/${currentUserId}`)
    )
    const logAccessTime = fbDatabase.ref(
      getRef(`${datePath}/${scheduleId}/teacher_room_logs/${currentUserId}`)
    )

    const serverTimestamp = await getServerTimestamp()
    await logAccessTime.push({
      teacher_id: currentUserId,
      access_time: serverTimestamp,
    })

    // 1. check existing room
    const snapshotOnce = (await roomListRef.once('value')).val()
    const foundRoom = Object.values(snapshotOnce || {}).find(
      (room) => +room?.teacher_id === +currentUserId
    )

    if (!isEmpty(foundRoom)) {
      return tryGetAccessLink({ class_id: foundRoom?.class_id, user_id: +currentUserId })
    }

    if (moment(current_time * 1000).isAfter(moment(begin_at * 1000).add({ minutes: 15 }))) {
      return
    }

    await waitingRoomRef.update({
      teacher_id: currentUserId,
      teacher_name,
      access_time: serverTimestamp,
      country: tester_waiting_room_info?.teacher_country || '',
      gender: tester_waiting_room_info?.teacher_gender || '',
      level_name: tester_waiting_room_info?.teacher_level_name || '',
    })

    if (isCancelled) return

    // 2. subscribe listener
    testRoomListener = roomListRef
    testRoomListener.on('value', async (snapshotRaw) => {
      if (isCancelled) return
      const snapshot = snapshotRaw?.val()
      const room = Object.values(snapshot || {}).find((r) => +r?.teacher_id === +currentUserId)
      if (room?.class_id) {
        if (testRoomListener) {
          testRoomListener.off('value')
          testRoomListener = null
        }
        if (expireTimeoutId) {
          clearTimeout(expireTimeoutId)
          expireTimeoutId = null
        }
        await tryGetAccessLink({ class_id: room.class_id, user_id: currentUserId })
      }
    })

    // 3. qua begin_at + 15 phút vẫn chưa có phòng -> complete
    if (!testRoomListener) return

    // serverOffset = current_time (server) - Date.now() (client) lúc nhận bookingInfo
    const nowMs = Date.now() + serverOffset
    const expireAt = moment(begin_at * 1000)
      .add({ minutes: 15 })
      .valueOf()
    const expireIn = Math.max(expireAt - nowMs, 0)
    expireTimeoutId = setTimeout(() => {
      expireTimeoutId = null
      if (isCancelled || !testRoomListener) return
      cleanup()
      onExpired?.()
    }, expireIn)
  }

  return {
    subscribe,
    unsubscribe: cleanup,
  }
}
